import { isVectorSource } from '../data/model';
import { evaluateAttributeRule } from '../rules/evaluate';
import { effectiveVectorSourceForLayer } from '../joins/effectiveLayer';
import { selectedFeatureRef, type SelectedFeatureRef } from '../selection/model';
import type { MapLibreRenderSource, MapLibreSelectableLayer } from './mapLibreRenderPlan';
import type { RenderScene } from './renderingAdapter';

export interface MapLibreSelectableFeatureLike {
  readonly id?: unknown;
  readonly layer?: { readonly id?: unknown };
}

interface SelectionCandidate {
  readonly feature: SelectedFeatureRef;
  readonly queryIndex: number;
  readonly stackIndex: number;
}

function canonicalSourceFeatureIndex(
  feature: MapLibreSelectableFeatureLike,
  mapping: MapLibreSelectableLayer,
  renderSources: readonly MapLibreRenderSource[],
  scene: RenderScene,
): number | null {
  const renderSource = renderSources.find((source) => source.id === mapping.renderSourceId);
  if (
    renderSource &&
    typeof feature.id === 'number' &&
    Number.isInteger(feature.id) &&
    feature.id >= 0
  ) {
    const sourceFeatureIndex = renderSource.sourceFeatureIndices[feature.id];
    if (sourceFeatureIndex !== undefined) return sourceFeatureIndex;
  }

  if (typeof feature.id !== 'string' && typeof feature.id !== 'number') return null;
  const source = scene.sources.find((candidate) => candidate.id === mapping.sourceId);
  if (!source || !isVectorSource(source)) return null;
  const index = source.data.features.findIndex((candidate) => candidate.id === feature.id);
  return index >= 0 ? index : null;
}

export function canonicalSelectionCandidates(
  features: readonly MapLibreSelectableFeatureLike[],
  selectableLayers: readonly MapLibreSelectableLayer[],
  renderSources: readonly MapLibreRenderSource[],
  scene: RenderScene,
): readonly SelectedFeatureRef[] {
  const mappingByRenderLayer = new Map(
    selectableLayers.map((mapping) => [mapping.renderLayerId, mapping]),
  );
  const layersById = new Map(scene.layers.map((layer) => [layer.id, layer]));
  const sourcesById = new Map(
    scene.sources.filter(isVectorSource).map((source) => [source.id, source]),
  );
  const effectiveSourcesByLayer = new Map(
    scene.layers.map((layer) => [layer.id, effectiveVectorSourceForLayer(scene.sources, layer)]),
  );
  const seen = new Set<string>();
  const candidates: SelectionCandidate[] = [];

  features.forEach((feature, queryIndex) => {
    const renderLayerId = feature.layer?.id;
    if (typeof renderLayerId !== 'string') return;
    const mapping = mappingByRenderLayer.get(renderLayerId);
    if (!mapping) return;
    const layer = layersById.get(mapping.layerId);
    const source = sourcesById.get(mapping.sourceId);
    if (!layer || !source || layer.sourceId !== source.id || !layer.visible) return;
    const sourceFeatureIndex = canonicalSourceFeatureIndex(feature, mapping, renderSources, scene);
    if (sourceFeatureIndex === null) return;
    const canonicalFeature = source.data.features[sourceFeatureIndex];
    if (!canonicalFeature) return;
    const effectiveFeature = effectiveSourcesByLayer.get(layer.id)?.data.features[
      sourceFeatureIndex
    ];
    if (
      !effectiveFeature ||
      !evaluateAttributeRule(layer.displayFilter, effectiveFeature.properties)
    ) {
      return;
    }
    const selected = selectedFeatureRef(layer.id, source.id, canonicalFeature, sourceFeatureIndex);
    if (seen.has(selected.key)) return;
    seen.add(selected.key);
    candidates.push({ feature: selected, queryIndex, stackIndex: mapping.stackIndex });
  });

  return candidates
    .sort((left, right) => left.stackIndex - right.stackIndex || left.queryIndex - right.queryIndex)
    .map((candidate) => candidate.feature);
}

export function resolveMapLibreSelectionFeature(
  features: readonly MapLibreSelectableFeatureLike[],
  selectableLayers: readonly MapLibreSelectableLayer[],
  renderSources: readonly MapLibreRenderSource[],
  scene: RenderScene,
): SelectedFeatureRef | null {
  return canonicalSelectionCandidates(features, selectableLayers, renderSources, scene)[0] ?? null;
}
